import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { useFiscalYear } from "@/contexts/FiscalYearContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpenseComparison = ({ user, adminViewMode, adminSelectedLocation }) => {
  // Fiscal Year Context
  const { fiscalYears, selectedFiscalYear, changeFiscalYear } = useFiscalYear();
  
  const [comparisonData, setComparisonData] = useState(null);
  const [ytdComparisonData, setYtdComparisonData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("mtd");

  // Detect if user has access to only one location OR if admin is viewing as single location
  const isActualSingleLocation = user && user.assigned_locations && user.assigned_locations.length === 1;
  const isAdminSingleMode = user && user.role === 'admin' && adminViewMode === 'single' && adminSelectedLocation;
  const isSingleLocation = isActualSingleLocation || isAdminSingleMode;
  const userLocation = isActualSingleLocation ? user.assigned_locations[0] : 
                      (isAdminSingleMode ? adminSelectedLocation : null);

  // State for single-location time-series data
  const [timeSeriesData, setTimeSeriesData] = useState(null);
  const [ytdTimeSeriesData, setYtdTimeSeriesData] = useState(null);

  useEffect(() => {
    if (selectedFiscalYear) {
      loadPeriods();
    }
  }, [selectedFiscalYear]);

  useEffect(() => {
    if (activeTab === "mtd") {
      if (isSingleLocation) {
        loadTimeSeriesData();
      } else if (selectedPeriod) {
        loadComparisonData();
      }
    }
  }, [selectedPeriod, activeTab, isSingleLocation]);

  useEffect(() => {
    if (activeTab === "ytd") {
      if (isSingleLocation) {
        loadYtdComparisonData(); // This will load YTD time-series for single location
      } else if (selectedFiscalYear) {
        loadYtdComparisonData(); // This will load cross-location comparison
      }
    }
  }, [selectedFiscalYear, activeTab, isSingleLocation]);

  const loadPeriods = async () => {
    try {
      const cacheBreaker = new Date().getTime();
      const params = selectedFiscalYear ? { fiscal_year: selectedFiscalYear } : {};
      const response = await axios.get(`${API}/periods?_=${cacheBreaker}`, { params });
      setPeriods(response.data.periods);
      if (response.data.periods.length > 0) {
        setSelectedPeriod(response.data.periods[0]);
      }
    } catch (error) {
      console.error("Error loading periods:", error);
    }
  };

  const loadComparisonData = async () => {
    setLoading(true);
    try {
      const cacheBreaker = new Date().getTime();
      const response = await axios.get(`${API}/pl/cross-location-comparison?_=${cacheBreaker}`, {
        params: { period: selectedPeriod }
      });
      setComparisonData(response.data);
    } catch (error) {
      console.error("Error loading comparison data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadYtdComparisonData = async () => {
    setLoading(true);
    try {
      const cacheBreaker = new Date().getTime();
      if (isSingleLocation) {
        // Load time-series data for single location
        const response = await axios.get(`${API}/ytd/time-series/${userLocation}?_=${cacheBreaker}`);
        setYtdTimeSeriesData(response.data);
      } else {
        // Load cross-location comparison data
        const response = await axios.get(`${API}/ytd/comparison/${selectedFiscalYear}?_=${cacheBreaker}`);
        setYtdComparisonData(response.data);
      }
    } catch (error) {
      console.error("Error loading YTD comparison data:", error);
      if (isSingleLocation) {
        setYtdTimeSeriesData(null);
      } else {
        setYtdComparisonData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTimeSeriesData = async () => {
    if (!isSingleLocation) return;
    
    setLoading(true);
    try {
      const cacheBreaker = new Date().getTime();
      const params = selectedFiscalYear ? { fiscal_year: selectedFiscalYear } : {};
      const response = await axios.get(`${API}/pl/time-series/${userLocation}?_=${cacheBreaker}`, { params });
      setTimeSeriesData(response.data);
    } catch (error) {
      console.error("Error loading time-series data:", error);
      setTimeSeriesData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getCellColor = (value, minValue, maxValue) => {
    if (value === minValue) {
      return 'bg-emerald-50 text-emerald-700 font-semibold';
    } else if (value === maxValue) {
      return 'bg-rose-50 text-rose-700 font-semibold';
    }
    return '';
  };

  const getMarginColor = (margin, threshold) => {
    if (margin >= threshold) {
      return 'bg-emerald-100 text-emerald-700';
    }
    return 'bg-rose-100 text-rose-700';
  };

  // Time-series view for single-location users
  const TimeSeriesExpenseView = ({ data, isYtd = false }) => {
    if (!data) {
      return (
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-slate-600">No time-series data available</p>
          </CardContent>
        </Card>
      );
    }

    const periods = isYtd ? data.years : data.periods;
    const timeSeriesData = isYtd ? data.ytd_time_series : data.time_series;
    const trends = isYtd ? data.ytd_trends : data.trends;
    
    // Get all unique expense names across all periods
    const allExpenses = new Set();
    Object.values(timeSeriesData).forEach(periodData => {
      Object.keys(periodData).forEach(expense => allExpenses.add(expense));
    });
    const expenseList = Array.from(allExpenses).sort();

    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800">
            {isYtd ? "YTD Expense Trends Over Time" : "Monthly Expense Trends Over Time"} - {data.location}
          </h3>
          <p className="text-sm text-slate-600">
            {isYtd ? "Year-to-date expenses across multiple years" : "Monthly expenses across recent periods"}
          </p>
          {(data.trend_periods?.current || data.trend_years?.current) && (
            <p className="text-xs text-slate-500 mt-1">
              Latest trends compare {data.trend_periods?.current || data.trend_years?.current} vs {data.trend_periods?.previous || data.trend_years?.previous}
            </p>
          )}
        </div>

        {/* Time-series table */}
        <Card className="border-slate-200">
          <CardHeader className="bg-slate-50 border-b border-slate-200">
            <CardTitle className="text-slate-800">Expense History</CardTitle>
            <CardDescription>Track expense changes over time</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-3 font-semibold text-slate-800 bg-slate-50 sticky left-0 z-10">
                      Expense Item
                    </th>
                    {periods.map((period) => (
                      <th key={period} className="text-center p-3 font-semibold text-slate-800 bg-slate-50 min-w-32">
                        {period}
                      </th>
                    ))}
                    <th className="text-center p-3 font-semibold text-slate-800 bg-slate-50">
                      Trend
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {expenseList.map((expenseName) => {
                    const trend = trends[expenseName];
                    let trendIcon = '';
                    let trendTooltip = '';
                    
                    if (trend) {
                      // Handle both data formats (YTD uses current/previous/change_pct, MTD uses current_amount/previous_amount/percentage_change)
                      const currentAmount = trend.current_amount ?? trend.current ?? 0;
                      const previousAmount = trend.previous_amount ?? trend.previous ?? 0;
                      const percentageChange = trend.percentage_change ?? trend.change_pct ?? 0;
                      
                      if (percentageChange > 5) {
                        trendIcon = '📈';
                      } else if (percentageChange < -5) {
                        trendIcon = '📉';
                      } else {
                        trendIcon = '➡️';
                      }
                      
                      if (previousAmount === 0 && currentAmount > 0) {
                        trendTooltip = `New expense: $${currentAmount.toFixed(2)}`;
                      } else if (currentAmount === 0 && previousAmount > 0) {
                        trendTooltip = `Expense eliminated: was $${previousAmount.toFixed(2)}`;
                      } else {
                        const changeText = percentageChange > 0 ? `+${percentageChange.toFixed(1)}%` : `${percentageChange.toFixed(1)}%`;
                        trendTooltip = `${changeText} vs previous period\n$${previousAmount.toFixed(2)} → $${currentAmount.toFixed(2)}`;
                      }
                    }

                    return (
                      <tr key={expenseName} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 font-medium text-slate-800 bg-white sticky left-0 z-10 border-r border-slate-200">
                          {expenseName}
                        </td>
                        {periods.map((period) => {
                          const amount = timeSeriesData[period]?.[expenseName] || 0;
                          return (
                            <td key={period} className="p-3 text-center">
                              {amount !== 0 ? formatCurrency(amount) : '-'}
                            </td>
                          );
                        })}
                        <td className="p-3 text-center">
                          {trendIcon && (
                            <span 
                              className="text-lg cursor-help relative group"
                              title={trendTooltip}
                            >
                              {trendIcon}
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-pre-line opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 min-w-max">
                                {trendTooltip}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-gray-900"></div>
                              </div>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="border-slate-200">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-slate-600 font-medium">Trends:</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">📈</span>
                <span className="text-slate-600">Expenses increased</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">📉</span>
                <span className="text-slate-600">Expenses decreased</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">➡️</span>
                <span className="text-slate-600">Expenses stable</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Extract the comparison content into a reusable component
  const ExpenseComparisonContent = ({ data, isYtd = false, periodSelector }) => {
    if (!data || !data.locations || (!data.summary && !data.summary_comparison) || !data.expense_comparison) {
      return (
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-slate-600">No data available for comparison</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        {/* Period Selector */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              {isYtd ? "YTD Expense Comparison Across Locations" : "Monthly Expense Comparison Across Locations"}
            </h3>
            <p className="text-sm text-slate-600">Green highlights lowest expenses, Red highlights highest</p>
          </div>
          {periodSelector}
        </div>

        {/* Combined Totals Summary */}
        <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
          <CardHeader className="bg-slate-100 border-b border-slate-200">
            <CardTitle className="text-slate-800">
              {isYtd ? "YTD Financial Summary - All Locations" : "Monthly Financial Summary - All Locations"}
            </CardTitle>
            <CardDescription>
              {isYtd ? "Year-to-date" : "Monthly"} Revenue, COGS, Expenses, and Net Profit/Loss across all locations
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {(() => {
              // Calculate combined totals across all locations
              // Handle both monthly (summary) and YTD (summary_comparison) formats
              const summaryData = data.summary || data.summary_comparison || {};
              
              const totals = data.locations.reduce((acc, location) => {
                const locationData = summaryData[location];
                if (locationData) {
                  // Handle both formats - monthly uses 'income', YTD uses 'total_income'
                  const income = locationData.income || locationData.total_income || 0;
                  const cogs = locationData.cogs || locationData.total_cogs || 0;
                  const expenses = locationData.operating_expenses || locationData.total_expenses || 0;
                  
                  acc.totalRevenue += Math.abs(income);
                  acc.totalCOGS += Math.abs(cogs);
                  acc.totalExpenses += Math.abs(expenses);
                  acc.totalExpensesPlusCOGS += Math.abs(expenses + cogs);
                  acc.netProfitLoss += (income - (expenses + cogs));
                }
                return acc;
              }, {
                totalRevenue: 0,
                totalCOGS: 0,
                totalExpenses: 0,
                totalExpensesPlusCOGS: 0,
                netProfitLoss: 0
              });

              return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-800 mb-1">Total Revenue</p>
                    <p className="text-xl font-bold text-green-900">{formatCurrency(totals.totalRevenue)}</p>
                  </div>
                  
                  <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-1">Total COGS</p>
                    <p className="text-xl font-bold text-amber-900">{formatCurrency(totals.totalCOGS)}</p>
                  </div>
                  
                  <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-medium text-red-800 mb-1">Operating Expenses</p>
                    <p className="text-xl font-bold text-red-900">{formatCurrency(totals.totalExpenses)}</p>
                  </div>
                  
                  <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-300">
                    <p className="text-sm font-medium text-slate-800 mb-1">Total COGS + Expenses</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(totals.totalExpensesPlusCOGS)}</p>
                  </div>
                  
                  <div className={`text-center p-4 rounded-lg border-2 ${totals.netProfitLoss >= 0 
                    ? 'bg-emerald-50 border-emerald-300' 
                    : 'bg-rose-50 border-rose-300'}`}>
                    <p className={`text-sm font-medium mb-1 ${totals.netProfitLoss >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                      Net Profit/Loss
                    </p>
                    <p className={`text-xl font-bold ${totals.netProfitLoss >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                      {formatCurrency(totals.netProfitLoss)}
                    </p>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Margin Summary Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {data.locations.map((location) => {
            // Handle both monthly (summary) and YTD (summary_comparison) formats
            const summaryData = data.summary || data.summary_comparison || {};
            const locationData = summaryData[location];
            if (!locationData) return null;
            
            return (
              <Card key={location} className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{location}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Gross Profit Margin</p>
                    <div className={`px-2 py-1 rounded text-center ${getMarginColor((() => {
                      // Calculate margin if not provided (for YTD data)
                      if (locationData.gross_profit_margin !== undefined) {
                        return locationData.gross_profit_margin;
                      }
                      // Calculate from YTD data: gross_profit / total_income * 100
                      const income = locationData.total_income || locationData.income || 0;
                      const grossProfit = locationData.gross_profit || 0;
                      return income > 0 ? (grossProfit / income * 100) : 0;
                    })(), 20)}`}>
                      <span className="font-bold">{(() => {
                        // Calculate margin if not provided (for YTD data)
                        if (locationData.gross_profit_margin !== undefined) {
                          return locationData.gross_profit_margin.toFixed(1);
                        }
                        // Calculate from YTD data: gross_profit / total_income * 100
                        const income = locationData.total_income || locationData.income || 0;
                        const grossProfit = locationData.gross_profit || 0;
                        const margin = income > 0 ? (grossProfit / income * 100) : 0;
                        return margin.toFixed(1);
                      })()}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Net Income Margin</p>
                    <div className={`px-2 py-1 rounded text-center ${getMarginColor((() => {
                      // Calculate margin if not provided (for YTD data)
                      if (locationData.net_income_margin !== undefined) {
                        return locationData.net_income_margin;
                      }
                      // Calculate from YTD data: net_income / total_income * 100
                      const income = locationData.total_income || locationData.income || 0;
                      const netIncome = locationData.net_income || 0;
                      return income > 0 ? (netIncome / income * 100) : 0;
                    })(), 3)}`}>
                      <span className="font-bold">{(() => {
                        // Calculate margin if not provided (for YTD data)
                        if (locationData.net_income_margin !== undefined) {
                          return locationData.net_income_margin.toFixed(1);
                        }
                        // Calculate from YTD data: net_income / total_income * 100
                        const income = locationData.total_income || locationData.income || 0;
                        const netIncome = locationData.net_income || 0;
                        const margin = income > 0 ? (netIncome / income * 100) : 0;
                        return margin.toFixed(1);
                      })()}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Detailed Expense Comparison Table */}
        <Card className="border-slate-200">
          <CardHeader className="bg-slate-50 border-b border-slate-200">
            <CardTitle className="text-slate-800">Detailed Expense Comparison</CardTitle>
            <CardDescription>Lower expenses highlighted in green, higher in red</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Modern Card-Based Layout */}
            <div className="space-y-4">
              {(() => {
                // Handle both array format (monthly) and dictionary format (YTD)
                let expenseArray = [];
                
                if (Array.isArray(data.expense_comparison)) {
                  // Monthly format: array of objects
                  expenseArray = data.expense_comparison;
                } else if (typeof data.expense_comparison === 'object') {
                  // YTD format: dictionary - convert to array format
                  expenseArray = Object.entries(data.expense_comparison).map(([expenseName, byLocation]) => ({
                    expense_name: expenseName,
                    by_location: byLocation
                  }));
                }
                
                return expenseArray.map((expense, idx) => {
                const allAmounts = Object.values(expense.by_location).map(val => Number(val) || 0).filter(val => val !== 0);
                const positiveAmounts = allAmounts.filter(val => val > 0);
                const minAmount = positiveAmounts.length > 0 ? Math.min(...positiveAmounts) : 0;
                const maxAmount = positiveAmounts.length > 0 ? Math.max(...positiveAmounts) : 0;
                
                return (
                  <Card key={idx} className="border-slate-200 hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg font-semibold text-slate-800">{expense.expense_name}</CardTitle>
                        <div className="text-sm text-slate-500">
                          {positiveAmounts.length > 0 ? `Range: ${formatCurrency(minAmount)} - ${formatCurrency(maxAmount)}` : 'No positive amounts'}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-4">
                        {data.locations.map((location) => {
                          const amount = Number(expense.by_location[location]) || 0;
                          
                          let bgColor = 'bg-slate-50 border-slate-200';
                          let textColor = 'text-slate-800';
                          let indicator = '';
                          
                          if (amount > 0 && positiveAmounts.length > 1) {
                            if (amount === minAmount) {
                              bgColor = 'bg-emerald-100 border-emerald-300';
                              textColor = 'text-emerald-800';
                              indicator = '🟢 Best';
                            } else if (amount === maxAmount) {
                              bgColor = 'bg-red-100 border-red-300';
                              textColor = 'text-red-800';
                              indicator = '🔴 Highest';
                            }
                          } else if (amount < 0) {
                            bgColor = 'bg-orange-100 border-orange-300';
                            textColor = 'text-orange-800';
                            indicator = '⚠️ Credit/Adj';
                          }

                          // Get trend indicator for both YTD and MTD data
                          let trendIcon = '';
                          let trendTooltip = '';
                          if (data.expense_trends && data.expense_trends[expense.expense_name] && data.expense_trends[expense.expense_name][location]) {
                            const trendData = data.expense_trends[expense.expense_name][location];
                            const trend = trendData.trend;
                            const percentageChange = trendData.percentage_change;
                            const currentAmount = trendData.current_amount;
                            const previousAmount = trendData.previous_amount;
                            
                            if (trend === 'up') {
                              trendIcon = '📈'; // Green up arrow - expenses increased
                            } else if (trend === 'down') {
                              trendIcon = '📉'; // Red down arrow - expenses decreased (good)
                            } else {
                              trendIcon = '➡️'; // Neutral - roughly same
                            }
                            
                            // Create tooltip text
                            if (previousAmount === 0 && currentAmount > 0) {
                              trendTooltip = `New expense: $${currentAmount.toFixed(2)}`;
                            } else if (currentAmount === 0 && previousAmount > 0) {
                              trendTooltip = `Expense eliminated: was $${previousAmount.toFixed(2)}`;
                            } else {
                              const changeText = percentageChange > 0 ? `+${percentageChange.toFixed(1)}%` : `${percentageChange.toFixed(1)}%`;
                              trendTooltip = `${changeText} vs previous month\n$${previousAmount.toFixed(2)} → $${currentAmount.toFixed(2)}`;
                            }
                          }
                          
                          return (
                            <div key={location} className={`p-3 rounded-lg border ${bgColor} ${textColor} text-center`}>
                              <div className="font-semibold text-sm mb-1 flex items-center justify-center gap-1">
                                <span>{location}</span>
                                {trendIcon && (
                                  <span 
                                    className="text-xs cursor-help relative group"
                                    title={trendTooltip}
                                  >
                                    {trendIcon}
                                    {/* Tooltip */}
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-pre-line opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 min-w-max">
                                      {trendTooltip}
                                      {/* Arrow */}
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-gray-900"></div>
                                    </div>
                                  </span>
                                )}
                              </div>
                              <div className="text-lg font-bold">
                                {amount !== 0 ? formatCurrency(amount) : '-'}
                              </div>
                              {indicator && (
                                <div className="text-xs mt-1 font-medium">{indicator}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Total Across All Stores */}
                      <div className="border-t border-slate-200 pt-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                          <div className="text-sm font-medium text-blue-700 mb-1">Total Across All Stores</div>
                          <div className="text-xl font-bold text-blue-900">
                            {formatCurrency(Object.values(expense.by_location).reduce((sum, amount) => {
                              const numAmount = Number(amount) || 0;
                              return sum + numAmount;
                            }, 0))}
                          </div>
                          <div className="text-xs text-blue-600 mt-1">
                            {data.locations.filter(loc => Number(expense.by_location[loc]) !== 0).length} locations with this expense
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
                });
              })()}
            </div>

            {/* Summary Totals Section */}
            <Card className="border-slate-200 bg-slate-50 mt-6">
              <CardHeader>
                <CardTitle className="text-lg text-slate-800">Financial Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Operating Expenses */}
                  <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
                    <p className="text-sm font-medium text-slate-600 mb-2">Operating Expenses</p>
                    <div className="space-y-1">
                      {data.locations.map((location) => {
                        const summaryData = data.summary || data.summary_comparison || {};
                        const locationData = summaryData[location];
                        return (
                          <div key={location} className="flex justify-between text-sm">
                            <span className="text-slate-600">{location}:</span>
                            <span className="font-semibold text-slate-800">
                              {locationData ? formatCurrency(locationData.operating_expenses || locationData.total_expenses) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* COGS */}
                  <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-2">Cost of Goods Sold</p>
                    <div className="space-y-1">
                      {data.locations.map((location) => {
                        const summaryData = data.summary || data.summary_comparison || {};
                        const locationData = summaryData[location];
                        return (
                          <div key={location} className="flex justify-between text-sm">
                            <span className="text-amber-600">{location}:</span>
                            <span className="font-semibold text-amber-800">
                              {locationData ? formatCurrency(locationData.cogs || locationData.total_cogs) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gross Profit */}
                  <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm font-medium text-emerald-800 mb-2">Gross Profit</p>
                    <div className="space-y-1">
                      {data.locations.map((location) => {
                        const summaryData = data.summary || data.summary_comparison || {};
                        const locationData = summaryData[location];
                        const grossProfit = locationData ? (locationData.gross_profit || 0) : 0;
                        return (
                          <div key={location} className="flex justify-between text-sm">
                            <span className="text-emerald-600">{location}:</span>
                            <span className={`font-semibold ${grossProfit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                              {locationData ? formatCurrency(grossProfit) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Net Income */}
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-800 mb-2">Net Income/Loss</p>
                    <div className="space-y-1">
                      {data.locations.map((location) => {
                        const summaryData = data.summary || data.summary_comparison || {};
                        const locationData = summaryData[location];
                        const netIncome = locationData ? (locationData.net_income || 0) : 0;
                        return (
                          <div key={location} className="flex justify-between text-sm">
                            <span className="text-blue-600">{location}:</span>
                            <span className={`font-semibold ${netIncome >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                              {locationData ? formatCurrency(netIncome) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="border-slate-200">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-emerald-50 border border-emerald-200 rounded"></div>
                <span className="text-slate-600">Lowest expense (best)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-rose-50 border border-rose-200 rounded"></div>
                <span className="text-slate-600">Highest expense (review needed)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-50 border border-orange-200 rounded"></div>
                <span className="text-slate-600">Credit/Adjustment (negative amount)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-4 bg-emerald-100 rounded"></div>
                <span className="text-slate-600">Margin ≥ Target (Green: GP ≥20%, NI ≥3%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-4 bg-rose-100 rounded"></div>
                <span className="text-slate-600">Margin &lt; Target (Red: GP &lt;20%, NI &lt;3%)</span>
              </div>
              {data.trend_periods && data.trend_periods.current && (
                <>
                  <div className="flex items-center gap-2 border-l border-slate-200 pl-6">
                    <span className="text-slate-600 font-medium">Monthly Trends:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📈</span>
                    <span className="text-slate-600">Expenses increased vs previous month</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📉</span>
                    <span className="text-slate-600">Expenses decreased vs previous month</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">➡️</span>
                    <span className="text-slate-600">Expenses stable (±5%)</span>
                  </div>
                </>
              )}
            </div>
            {data.trend_periods && data.trend_periods.current && (
              <div className="text-xs text-slate-500 mt-2">
                Trends compare {data.trend_periods.current} vs {data.trend_periods.previous}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="mtd">Month-to-Date (MTD)</TabsTrigger>
          <TabsTrigger value="ytd">Year-to-Date (YTD)</TabsTrigger>
        </TabsList>
        
        <TabsContent value="mtd" className="space-y-6">
          {isSingleLocation ? (
            <TimeSeriesExpenseView 
              data={timeSeriesData} 
              isYtd={false}
            />
          ) : (
            <ExpenseComparisonContent 
              data={comparisonData} 
              isYtd={false}
              periodSelector={
                periods.length > 0 && (
                  <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      {periods.map((period) => (
                        <SelectItem key={period} value={period}>{period}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
            />
          )}
        </TabsContent>
        
        <TabsContent value="ytd" className="space-y-6">
          {isSingleLocation ? (
            <TimeSeriesExpenseView 
              data={ytdTimeSeriesData} 
              isYtd={true}
            />
          ) : (
            <ExpenseComparisonContent 
              data={ytdComparisonData} 
              isYtd={true}
              periodSelector={
                fiscalYears.length > 0 && (
                  <Select value={selectedFiscalYear} onValueChange={changeFiscalYear}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select Fiscal Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {fiscalYears.map((year) => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );

};

export default ExpenseComparison;
